import {
  CCOrganization,
  CCSite,
  CCOrganizationsResponse,
  CCOrgSitesResponse,
  CCOrganizationUpdateRequest,
  CCDefaultConfig,
  CCDefaultConfigUpdateRequest,
  CCOrgAdmins,
  CCOrganizationsSummary,
  CCProviderModelRegistryEntry,
  CCOotbModelConfiguration,
  CCOotbModelConfigurationRequest,
  CCGroupedConfig,
  CCOotbAllowlistEntry,
  CCCustomAllowlistEntry,
  CCAllowlistUnselectUsage,
  CCOrgModelUnselectUsage,
  CCOrgModelEntry,
  CCModelUtility,
  CCUtilityAssignmentEntry,
  CCSystemDefaultEntry,
  CCSystemDefaults,
  CCOrgRegistryModelEntry,
  CCCreateCustomModelConfigurationRequest,
  CCUpdateOrgModelConfigurationRequest,
  ModelProvider,
  ModelRegistryType,
  CCDomainUtilityOptionsPageResponse,
  CCDomainUtilityRequirementsStatus,
  CCDomainConfigurationResponse,
} from '../../types';
import { API_CONFIG, CC_ENDPOINTS, ORG_MODELS_ENDPOINTS, DOMAIN_MODEL_ENDPOINTS } from '../../config/config';
import { getHeadersWithOrg, authFetch } from './client';
import type { ColumnFilter } from '../../components/common/FilterableTable/types';
import { buildPaginatedQueryParams, type SortOptions } from './paginationParams';
import type { DomainSortKey, OverviewDomainsResponse, SortDirection } from '../../types/overview';
import { validateOverviewDomainsResponse } from './overviewValidators';
import { flattenGroupedConfig } from '../../utils/modelConfig';

// Re-export for callers that already import these from this module.
export { buildPaginatedQueryParams };
export type { SortOptions };

// =============================================================================
// Organizations API Functions
// =============================================================================

// GET /api/command-center/organizations - Paginated org list
export async function fetchCCOrganizations(
  page: number = 1,
  pageSize: number = 10,
  filters?: ColumnFilter[],
  sort?: SortOptions,
): Promise<CCOrganizationsResponse> {
  const params = buildPaginatedQueryParams(page, pageSize, filters, sort);
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORGANIZATIONS}?${params.toString()}`;

  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch organizations: ${response.status}`);
  }

  return response.json();
}

// GET /api/command-center/organizations-summary - Aggregated org stats
export async function fetchCCOrganizationsSummary(): Promise<CCOrganizationsSummary> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORGANIZATIONS_SUMMARY}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch organizations summary: ${response.status}`);
  }

  return response.json();
}

// GET /api/command-center/organization-summary/{org_id} - Single org summary
export async function fetchCCOrganizationSummary(orgId: string): Promise<CCOrganization> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORGANIZATION_SUMMARY(orgId)}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch organization summary: ${response.status}`);
  }

  return response.json();
}

// PUT /api/command-center/organizations/{org_id} - Update org settings (204 No Content)
export async function updateCCOrganization(
  orgId: string,
  payload: CCOrganizationUpdateRequest,
): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.UPDATE_ORGANIZATION(orgId)}`;
  const response = await authFetch(url, {
    method: 'PUT',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update organization: ${response.status}`);
  }
}

// =============================================================================
// Sites API Functions
// =============================================================================

// GET /api/command-center/organizations/{org_id}/sites - Paginated site list
export async function fetchCCOrgSites(
  orgId: string,
  page: number = 1,
  pageSize: number = 10,
  filters?: ColumnFilter[],
  sort?: SortOptions,
): Promise<CCOrgSitesResponse> {
  const params = buildPaginatedQueryParams(page, pageSize, filters, sort);
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_SITES(orgId)}?${params.toString()}`;

  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch sites: ${response.status}`);
  }

  return response.json();
}

export interface CCOrgAnalyticsDomainsParams {
  page?: number;
  pageSize?: number;
  sortBy?: DomainSortKey;
  sortDir?: SortDirection;
}

// GET /platform/api/v1/command-center/organizations/{org_id}/analytics/domains
// Same request/response contract as overview.ts's fetchOverviewDomains (both
// return OverviewDomainsResponse) — this is the Command Center org-detail
// page's own copy of that endpoint, scoped under /command-center/ rather
// than the general org overview dashboard's /orgs/ path.
export async function fetchCCOrgAnalyticsDomains(
  orgId: string,
  params: CCOrgAnalyticsDomainsParams = {},
): Promise<OverviewDomainsResponse> {
  const { page = 1, pageSize = 50, sortBy = 'site_name', sortDir = 'asc' } = params;
  const qs = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
    sort_by: sortBy,
    sort_dir: sortDir,
  });
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ANALYTICS_DOMAINS(orgId)}?${qs.toString()}`;

  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch domains: ${response.status}`);
  }

  return validateOverviewDomainsResponse(await response.json());
}

// GET /api/command-center/organizations/{org_id}/sites/{site_id} - Single site detail
export async function fetchCCSiteDetail(
  siteId: string,
  orgId: string,
): Promise<CCSite> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_SITE_DETAIL(orgId, siteId)}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch site: ${response.status}`);
  }

  return response.json();
}

// GET /platform/api/v1/command-center/organizations/{org_id}/sites/{site_id}/configuration
// requirement 5 -- this domain's current per-utility configuration, redacted.
export async function fetchDomainConfiguration(
  orgId: string,
  siteId: string,
): Promise<CCDomainConfigurationResponse> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_SITE_CONFIGURATION(orgId, siteId)}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch domain configuration: ${response.status}`);
  }

  return response.json();
}

// GET /platform/api/v1/orgs/{orgId}/domain-utilities/{utilityName}/configurations
// API 1 (requirement 2) -- one utility's allowlisted configuration options, paginated.
export async function fetchDomainUtilityConfigurations(
  orgId: string,
  utilityName: string,
  page: number = 1,
  pageSize: number = 20,
  search?: string,
): Promise<CCDomainUtilityOptionsPageResponse> {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (search) params.set('search', search);
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${DOMAIN_MODEL_ENDPOINTS.UTILITY_CONFIGURATIONS(orgId, utilityName)}?${params.toString()}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch configuration options: ${response.status}`);
  }

  return response.json();
}

// GET /platform/api/v1/orgs/{orgId}/domain-utilities/required-status
// API 2 -- can this org create a domain right now? Checked before the create-domain form opens.
export async function fetchDomainUtilityRequirementsStatus(orgId: string): Promise<CCDomainUtilityRequirementsStatus> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${DOMAIN_MODEL_ENDPOINTS.REQUIRED_STATUS(orgId)}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to check domain configuration requirements: ${response.status}`);
  }

  return response.json();
}

// =============================================================================
// Default Config API Functions
// =============================================================================

// GET /api/command-center/default-org-config - Get default org config
export async function fetchCCDefaultConfig(): Promise<CCDefaultConfig> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.DEFAULT_CONFIG}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch default config: ${response.status}`);
  }

  return response.json();
}

// PUT /api/command-center/default-org-config - Update default org config
export async function updateCCDefaultConfig(
  payload: CCDefaultConfigUpdateRequest,
): Promise<CCDefaultConfig> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.DEFAULT_CONFIG}`;
  const response = await authFetch(url, {
    method: 'PUT',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update default config: ${response.status}`);
  }

  return response.json();
}

// =============================================================================
// Org Admin API Functions
// =============================================================================

// GET /api/command-center/organizations/{org_id}/admins - Current org_admins
export async function fetchCCOrgAdmins(orgId: string): Promise<CCOrgAdmins> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ADMINS(orgId)}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    // FastAPI 422 returns `detail` as an array of validation-error objects, not a string.
    const detail = typeof errorData.detail === 'string' ? errorData.detail : null;
    throw new Error(detail || errorData.error || `Failed to fetch org admins: ${response.status}`);
  }

  return response.json();
}

// POST /api/command-center/organizations/{org_id}/admins - Grant org_admin to a user
export async function addCCOrgAdmin(orgId: string, userId: string): Promise<CCOrgAdmins> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ADMINS(orgId)}`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({ user_id: userId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    // FastAPI 422 returns `detail` as an array of validation-error objects, not a string.
    const detail = typeof errorData.detail === 'string' ? errorData.detail : null;
    throw new Error(detail || errorData.error || `Failed to add org admin: ${response.status}`);
  }

  return response.json();
}

// DELETE /api/command-center/organizations/{org_id}/admins/{user_id} - Revoke a specific org_admin
export async function revokeCCOrgAdmin(orgId: string, userId: string): Promise<CCOrgAdmins> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ADMIN_ENTRY(orgId, userId)}`;
  const response = await authFetch(url, {
    method: 'DELETE',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    // FastAPI 422 returns `detail` as an array of validation-error objects, not a string.
    const detail = typeof errorData.detail === 'string' ? errorData.detail : null;
    throw new Error(detail || errorData.error || `Failed to revoke org admin: ${response.status}`);
  }

  return response.json();
}

// =============================================================================
// Models API Functions
// =============================================================================

// GET /platform/api/v1/command-center/configurations/{configuration_id}/config
// — an OOTB configuration's stored config values, grouped by field class,
// with secrets returned as the REDACTED sentinel. Returned grouped (the wire
// shape); the dialog flattens it via flattenGroupedConfig() to seed the form.
export async function fetchModelConfigurationValues(id: string): Promise<CCGroupedConfig> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.OOTB_MODEL_CONFIGURATION_CONFIG(id)}`;
  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch configuration values: ${response.status}`);
  }

  const data: { config: CCGroupedConfig } = await response.json();
  return data.config;
}

interface ProviderModelRegistryEntryWire {
  id: string;
  provider: string;
  model: string;
  type: string;
  is_active: boolean;
  is_deprecated: boolean;
  sunset_date: string | null;
}

function mapProviderModelRegistryEntry(entry: ProviderModelRegistryEntryWire): CCProviderModelRegistryEntry {
  return {
    id: entry.id,
    provider: entry.provider as ModelProvider,
    model: entry.model,
    type: entry.type as ModelRegistryType,
    isActive: entry.is_active,
    isDeprecated: entry.is_deprecated,
    sunsetDate: entry.sunset_date,
  };
}

// The registry catalog is bounded by design (a fixed set of providers × their
// published models) — one page at the backend's max page_size covers it, so
// callers here don't need to paginate.
const PROVIDER_MODEL_REGISTRY_MAX_PAGE_SIZE = 500;

// GET /platform/api/v1/command-center/provider-model-registry — Group A,
// Adobe KG AI admin-gated, platform-wide (no org_id in the path). Filters/sort are
// JSON-encoded query params (this repo's existing list-endpoint convention —
// see app/api/analytics/_query_params.py — not bespoke per-column params).
// Only equality filters reach the backend (FILTERABLE_COLUMNS in
// provider_model_registry_repository.py is exact-match only, no substring/
// range) — provider/type/isDeprecated qualify (they're exact-match selects
// in the UI too); a free-text "model" search or a sunsetDate range still
// has to be applied by the caller on the returned rows.
export async function fetchProviderModelRegistry(
  filters?: { provider?: ModelProvider; type?: ModelRegistryType; isDeprecated?: boolean },
  sort?: SortOptions,
): Promise<CCProviderModelRegistryEntry[]> {
  const columnFilters: ColumnFilter[] = [];
  if (filters?.provider) columnFilters.push({ key: 'provider', value: filters.provider });
  if (filters?.type) columnFilters.push({ key: 'type', value: filters.type });
  if (filters?.isDeprecated !== undefined) columnFilters.push({ key: 'is_deprecated', value: String(filters.isDeprecated) });

  const params = buildPaginatedQueryParams(1, PROVIDER_MODEL_REGISTRY_MAX_PAGE_SIZE, columnFilters, sort);
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.PROVIDER_MODEL_REGISTRY}?${params.toString()}`;

  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch provider model registry: ${response.status}`);
  }

  const data: { entries: ProviderModelRegistryEntryWire[] } = await response.json();
  return data.entries.map(mapProviderModelRegistryEntry);
}

interface OotbModelConfigurationEntryWire {
  configuration_id: string;
  registry_id: string;
  configuration_name: string;
  configuration_description: string | null;
  provider: string;
  model: string;
  type: string;
  is_valid: boolean;
  is_deprecated: boolean;
  updated_by: string;
  updated_at: string;
}

function mapOotbModelConfiguration(entry: OotbModelConfigurationEntryWire): CCOotbModelConfiguration {
  return {
    id: entry.configuration_id,
    registryId: entry.registry_id,
    name: entry.configuration_name,
    description: entry.configuration_description ?? undefined,
    provider: entry.provider as ModelProvider,
    model: entry.model,
    type: entry.type as ModelRegistryType,
    isValid: entry.is_valid,
    isDeprecated: entry.is_deprecated,
    updatedAt: entry.updated_at,
    updatedBy: entry.updated_by,
  };
}

// Like the registry catalog, configured models are an admin-created,
// bounded set — one page at the backend's max page_size covers them, so
// callers never paginate.
const OOTB_MODEL_CONFIGURATIONS_MAX_PAGE_SIZE = 500;

// GET /platform/api/v1/command-center/configurations — global (no org_id),
// the Models page's "Configurations" tab. Filters/sort are JSON-encoded
// query params (same convention as the registry list). Only the exact-match
// filters (provider/type/is_valid/is_deprecated — all real columns on the
// response entry) go server-side; a free-text name/model search stays
// client-side, exactly as with the registry table. Returns the full page's
// entries (both callers — this tab and ModelAllowlistPanel — want the whole
// bounded list, not a wire-level page object).
export async function fetchOotbModelConfigurations(
  filters?: { provider?: ModelProvider; type?: ModelRegistryType; isValid?: boolean; isDeprecated?: boolean },
  sort?: SortOptions,
): Promise<CCOotbModelConfiguration[]> {
  const columnFilters: ColumnFilter[] = [];
  if (filters?.provider) columnFilters.push({ key: 'provider', value: filters.provider });
  if (filters?.type) columnFilters.push({ key: 'type', value: filters.type });
  if (filters?.isValid !== undefined) columnFilters.push({ key: 'is_valid', value: String(filters.isValid) });
  if (filters?.isDeprecated !== undefined) columnFilters.push({ key: 'is_deprecated', value: String(filters.isDeprecated) });

  const params = buildPaginatedQueryParams(1, OOTB_MODEL_CONFIGURATIONS_MAX_PAGE_SIZE, columnFilters, sort);
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.OOTB_MODEL_CONFIGURATIONS}?${params.toString()}`;

  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch model configurations: ${response.status}`);
  }

  const data: { entries: OotbModelConfigurationEntryWire[] } = await response.json();
  return data.entries.map(mapOotbModelConfiguration);
}

// POST /platform/api/v1/command-center/configurations — create. `config` is
// already grouped by field class (the dialog groups it before calling).
// registry_id pins provider+model, so the model isn't repeated in `config`.
// Returns the generated id (the only thing the response carries).
export async function createOotbModelConfiguration(
  payload: CCOotbModelConfigurationRequest,
): Promise<{ configurationId: string }> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.OOTB_MODEL_CONFIGURATIONS}`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({
      registry_id: payload.registryId,
      configuration_name: payload.name,
      configuration_description: payload.description ?? null,
      config: payload.config,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to create configuration: ${response.status}`);
  }

  const data: { configuration_id: string } = await response.json();
  return { configurationId: data.configuration_id };
}

// PUT /platform/api/v1/command-center/configurations/{configuration_id} —
// update. registry_id isn't sent: the model an OOTB configuration wraps is
// fixed at creation and the path already identifies the row. `config` is
// grouped by field class, same as create. Response is empty (200).
export async function updateOotbModelConfiguration(
  id: string,
  payload: CCOotbModelConfigurationRequest,
): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.OOTB_MODEL_CONFIGURATION(id)}`;
  const response = await authFetch(url, {
    method: 'PUT',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({
      configuration_name: payload.name,
      configuration_description: payload.description ?? null,
      config: payload.config,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update configuration: ${response.status}`);
  }
}

// DELETE /platform/api/v1/command-center/configurations/{configuration_id}
// — 204 No Content.
export async function deleteOotbModelConfiguration(id: string): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.OOTB_MODEL_CONFIGURATION(id)}`;
  const response = await authFetch(url, {
    method: 'DELETE',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to delete configuration: ${response.status}`);
  }
}

// PUT /platform/api/v1/command-center/provider-model-registry/{registry_id}
// — full-resource PUT (the backend's Pydantic model requires every field on
// the wire even though only is_active/is_deprecated/sunset_date are actually
// writable), so this takes the full entry rather than just the deprecation
// fields — the caller already has it in hand from the table row it's saving.
export async function setRegistryDeprecation(
  entry: CCProviderModelRegistryEntry,
): Promise<CCProviderModelRegistryEntry> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.PROVIDER_MODEL_REGISTRY_ENTRY(entry.id)}`;

  const response = await authFetch(url, {
    method: 'PUT',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({
      id: entry.id,
      provider: entry.provider,
      model: entry.model,
      type: entry.type,
      is_active: entry.isActive,
      is_deprecated: entry.isDeprecated,
      sunset_date: entry.sunsetDate,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update registry entry: ${response.status}`);
  }

  return mapProviderModelRegistryEntry(await response.json());
}

// Per-org allowlist. OOTB and Custom are two independent pools, each its own
// endpoint (see design-frames/org-detail.html's Model Allowlist tab) — never
// gated by a mode flag. Each list row carries its own is_allowed, resolved
// server-side; saving PATCHes only the rows whose is_allowed changed.

interface OotbAllowlistEntryWire {
  configuration_id: string;
  configuration_name: string;
  provider: string;
  model: string;
  type: string;
  is_allowed: boolean;
  is_allowlist_locked?: boolean;
}

interface CustomAllowlistEntryWire {
  registry_id: string;
  provider: string;
  model: string;
  type: string;
  is_allowed: boolean;
}

// Both allowlist pools are bounded per org (its configured/registry models),
// so one max-size page covers them and callers don't paginate.
const ORG_ALLOWLIST_MAX_PAGE_SIZE = 500;

// GET /platform/api/v1/command-center/{org_id}/allowlist/ootb
export async function fetchOrgOotbAllowlist(orgId: string): Promise<CCOotbAllowlistEntry[]> {
  const params = buildPaginatedQueryParams(1, ORG_ALLOWLIST_MAX_PAGE_SIZE);
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ALLOWLIST_OOTB(orgId)}?${params.toString()}`;
  const response = await authFetch(url, { method: 'GET', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch OOTB allowlist: ${response.status}`);
  }

  const data: { entries: OotbAllowlistEntryWire[] } = await response.json();
  return data.entries.map((e) => ({
    configurationId: e.configuration_id,
    configurationName: e.configuration_name,
    provider: e.provider as ModelProvider,
    model: e.model,
    type: e.type as ModelRegistryType,
    isAllowed: e.is_allowed,
    isAllowlistLocked: e.is_allowlist_locked ?? false,
  }));
}

// GET /platform/api/v1/command-center/{org_id}/allowlist/custom
export async function fetchOrgCustomAllowlist(orgId: string): Promise<CCCustomAllowlistEntry[]> {
  const params = buildPaginatedQueryParams(1, ORG_ALLOWLIST_MAX_PAGE_SIZE);
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ALLOWLIST_CUSTOM(orgId)}?${params.toString()}`;
  const response = await authFetch(url, { method: 'GET', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch custom allowlist: ${response.status}`);
  }

  const data: { entries: CustomAllowlistEntryWire[] } = await response.json();
  return data.entries.map((e) => ({
    registryId: e.registry_id,
    provider: e.provider as ModelProvider,
    model: e.model,
    type: e.type as ModelRegistryType,
    isAllowed: e.is_allowed,
  }));
}

// POST /platform/api/v1/command-center/{org_id}/allowlist/ootb — delta of
// only the rows whose is_allowed changed. 200, no body.
export async function updateOrgOotbAllowlist(
  orgId: string,
  changes: { configurationId: string; isAllowed: boolean }[],
): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ALLOWLIST_OOTB(orgId)}`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(changes.map((c) => ({ configuration_id: c.configurationId, is_allowed: c.isAllowed }))),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update OOTB allowlist: ${response.status}`);
  }
}

// POST /platform/api/v1/command-center/{org_id}/allowlist/custom — delta.
export async function updateOrgCustomAllowlist(
  orgId: string,
  changes: { registryId: string; isAllowed: boolean }[],
): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ALLOWLIST_CUSTOM(orgId)}`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(changes.map((c) => ({ registry_id: c.registryId, is_allowed: c.isAllowed }))),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update custom allowlist: ${response.status}`);
  }
}

interface UnselectUsageEntryWire {
  id: string;
  usage: { org_id: string; org_name: string; domain_count: number }[];
}

function mapUnselectUsageEntry(entry: UnselectUsageEntryWire): CCAllowlistUnselectUsage {
  return {
    id: entry.id,
    usage: entry.usage.map((u) => ({ orgId: u.org_id, orgName: u.org_name, domainCount: u.domain_count })),
  };
}

// POST /platform/api/v1/command-center/{org_id}/allowlist/ootb/usage — for
// each configuration_id about to be unselected, every org (not just this
// one — a shared OOTB configuration can be used elsewhere) with domains
// still referencing it. Called before Save Changes when the delta includes
// a revoke, to populate the confirm-before-save dialog.
export async function fetchOotbAllowlistUnselectUsage(
  orgId: string,
  configurationIds: string[],
): Promise<CCAllowlistUnselectUsage[]> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ALLOWLIST_OOTB(orgId)}/usage`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({ ids: configurationIds }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch OOTB allowlist usage: ${response.status}`);
  }

  const data: { entries: UnselectUsageEntryWire[] } = await response.json();
  return data.entries.map(mapUnselectUsageEntry);
}

// POST /platform/api/v1/command-center/{org_id}/allowlist/custom/usage — same
// shape as fetchOotbAllowlistUnselectUsage, keyed by registry_id instead.
export async function fetchCustomAllowlistUnselectUsage(
  orgId: string,
  registryIds: string[],
): Promise<CCAllowlistUnselectUsage[]> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.ORG_ALLOWLIST_CUSTOM(orgId)}/usage`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({ ids: registryIds }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch custom allowlist usage: ${response.status}`);
  }

  const data: { entries: UnselectUsageEntryWire[] } = await response.json();
  return data.entries.map(mapUnselectUsageEntry);
}

// =============================================================================
// Org Models Landing Page (self-service) — design-frames/12-landing-models.html
// Org-member-gated (require_org_from_path on the backend), NOT the CC-admin
// surface above — a distinct feature that happens to live in this file.
// Backed by two route groups under /platform/api/v1/models/{orgId}/...:
// Group D (org_model_configuration_api.py, list/create/update/delete/reset/
// allowlist-toggle/validate) and Group F (org_utility_assignment_api.py,
// the org's per-utility primary assignment).
// =============================================================================

const ORG_MODEL_CONFIGURATIONS_MAX_PAGE_SIZE = 500;

interface OrgModelConfigurationEntryWire {
  configuration_id: string;
  configuration_name: string;
  configuration_description?: string | null;
  provider: string;
  model: string;
  type: string;
  source: 'ootb' | 'custom';
  is_valid: boolean;
  is_deprecated: boolean;
  sunset_date?: string | null;
  is_overridden: boolean;
  is_allowed: boolean;
  is_allowlist_locked?: boolean;
  utilities?: string[];
}

function mapOrgModelConfigurationEntry(entry: OrgModelConfigurationEntryWire): CCOrgModelEntry {
  return {
    id: entry.configuration_id,
    name: entry.configuration_name,
    description: entry.configuration_description ?? undefined,
    provider: entry.provider as ModelProvider,
    model: entry.model,
    type: entry.type as ModelRegistryType,
    source: entry.source,
    isValid: entry.is_valid,
    isDeprecated: entry.is_deprecated,
    sunsetDate: entry.sunset_date ?? null,
    isOverridden: entry.is_overridden,
    allowed: entry.is_allowed,
    isAllowlistLocked: entry.is_allowlist_locked ?? false,
    utilities: (entry.utilities ?? []) as CCModelUtility[],
  };
}

// GET /platform/api/v1/models/{orgId}/configuration — union of platform OOTB
// configurations and this org's own Custom configurations, every flag
// (source/is_valid/is_deprecated/is_overridden/is_allowed/utilities) already
// merged server-side. Only the exact-match filters (allowed/provider/type/
// source/isValid/isDeprecated — real DB columns) go server-side; a free-text
// name/model search stays client-side, same split as the CC tables above.
export async function fetchOrgModelConfigurations(
  orgId: string,
  filters?: {
    allowed?: boolean; provider?: ModelProvider; type?: ModelRegistryType;
    source?: 'ootb' | 'custom'; isValid?: boolean; isDeprecated?: boolean;
  },
  sort?: SortOptions,
): Promise<CCOrgModelEntry[]> {
  const columnFilters: ColumnFilter[] = [];
  if (filters?.allowed !== undefined) columnFilters.push({ key: 'is_allowed', value: String(filters.allowed) });
  if (filters?.provider) columnFilters.push({ key: 'provider', value: filters.provider });
  if (filters?.type) columnFilters.push({ key: 'type', value: filters.type });
  if (filters?.source) columnFilters.push({ key: 'source', value: filters.source });
  if (filters?.isValid !== undefined) columnFilters.push({ key: 'is_valid', value: String(filters.isValid) });
  if (filters?.isDeprecated !== undefined) columnFilters.push({ key: 'is_deprecated', value: String(filters.isDeprecated) });

  const params = buildPaginatedQueryParams(1, ORG_MODEL_CONFIGURATIONS_MAX_PAGE_SIZE, columnFilters, sort);
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.CONFIGURATIONS(orgId)}?${params.toString()}`;
  const response = await authFetch(url, { method: 'GET', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch org model configurations: ${response.status}`);
  }

  const data: { entries: OrgModelConfigurationEntryWire[] } = await response.json();
  return data.entries.map(mapOrgModelConfigurationEntry);
}

// GET .../configuration/{id}/config — secrets redacted; behavioral only for
// an OOTB row (its credentials are never this org's to see). Grouped on the
// wire like the CC-admin equivalent; flattened here so callers (the dialog,
// the view drawer) work in the same flat field-name space either way.
export async function fetchOrgModelConfigurationValues(orgId: string, id: string): Promise<Record<string, unknown>> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.CONFIGURATION_CONFIG(orgId, id)}`;
  const response = await authFetch(url, { method: 'GET', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch configuration values: ${response.status}`);
  }

  const data: { config: CCGroupedConfig } = await response.json();
  return flattenGroupedConfig(data.config);
}

// GET .../registry/providers — Step A of the New Configuration wizard's
// picker: only providers with at least one model this org has
// custom-allowlisted for `type`, never the full global provider list (an
// org must only ever be offered what it's actually allowed to create
// against — matches the 403 create() would otherwise raise).
export async function fetchOrgRegistryProviders(orgId: string, type: ModelRegistryType): Promise<string[]> {
  const qs = new URLSearchParams({ type });
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.REGISTRY_PROVIDERS(orgId)}?${qs.toString()}`;
  const response = await authFetch(url, { method: 'GET', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch registry providers: ${response.status}`);
  }

  const data: { providers: string[] } = await response.json();
  return data.providers;
}

// GET .../registry/models — Step B of the wizard's picker, scoped the same
// way as the providers list. Carries is_deprecated/sunset_date so the
// picker can warn on a model that's already scheduled for sunset.
export async function fetchOrgRegistryModels(
  orgId: string,
  type: ModelRegistryType,
  provider: string,
): Promise<CCOrgRegistryModelEntry[]> {
  const qs = new URLSearchParams({ type, provider });
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.REGISTRY_MODELS(orgId)}?${qs.toString()}`;
  const response = await authFetch(url, { method: 'GET', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch registry models: ${response.status}`);
  }

  const data: { entries: { registry_id: string; model: string; is_deprecated: boolean; sunset_date: string | null }[] } =
    await response.json();
  return data.entries.map((e) => ({
    registryId: e.registry_id,
    model: e.model,
    isDeprecated: e.is_deprecated,
    sunsetDate: e.sunset_date,
  }));
}

// POST .../configuration — creates a Custom configuration. registry_id must
// be in this org's Custom candidate allowlist (403 otherwise) and the
// backend runs the same live validation create/update always enforce (400
// with "Model validation failed: ..." if it fails). Returns the new id only
// — allowed/utilities are set via the separate calls below, since neither is
// part of this request's contract.
export async function createOrgModelConfiguration(
  orgId: string,
  payload: CCCreateCustomModelConfigurationRequest,
): Promise<{ id: string }> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.CONFIGURATIONS(orgId)}`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({
      registry_id: payload.registryId,
      configuration_name: payload.name,
      configuration_description: payload.description,
      config: payload.config,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to create configuration: ${response.status}`);
  }

  const data: { configuration_id: string } = await response.json();
  return { id: data.configuration_id };
}

// PUT .../configuration/{id} — full edit for a Custom configuration
// (credentials merge REDACTED-vs-stored like the CC-admin equivalent), or a
// behavioral-override-only edit for an OOTB one (the backend 400s if `config`
// carries anything but the `behavioral` group for an OOTB id).
export async function updateOrgModelConfiguration(
  orgId: string,
  id: string,
  payload: CCUpdateOrgModelConfigurationRequest,
): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.CONFIGURATION(orgId, id)}`;
  const response = await authFetch(url, {
    method: 'PUT',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({
      configuration_name: payload.name,
      configuration_description: payload.description,
      config: payload.config,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update configuration: ${response.status}`);
  }
}

// DELETE .../configuration/{id} — Custom configurations only (400 for an
// OOTB id); 409 if a domain still references it.
export async function deleteOrgModelConfiguration(orgId: string, id: string): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.CONFIGURATION(orgId, id)}`;
  const response = await authFetch(url, { method: 'DELETE', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to delete configuration: ${response.status}`);
  }
}

// POST .../configuration/{id}/reset — OOTB configurations only; clears this
// org's behavioural-param override, reverting to the platform default.
export async function resetOrgModelConfiguration(orgId: string, id: string): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.CONFIGURATION_RESET(orgId, id)}`;
  const response = await authFetch(url, { method: 'POST', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to reset configuration: ${response.status}`);
  }
}

// POST .../configurations/allowlist — bulk delta toggle (only the named ids
// change), backing both a single row's checkbox and the table's batched
// "Save Allowlist Changes" action.
export async function applyOrgAllowlistChanges(
  orgId: string,
  changes: { id: string; isAllowed: boolean }[],
): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.CONFIGURATIONS_ALLOWLIST(orgId)}`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(changes.map((c) => ({ configuration_id: c.id, is_allowed: c.isAllowed }))),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update allowlist: ${response.status}`);
  }
}

// POST .../configurations/usage — domain usage (this org only) for
// configuration ids about to be unselected from this org's allowlist.
// Called before Save Allowlist Changes when the delta includes a revoke, to
// populate the confirm-before-save dialog.
export async function fetchOrgModelUnselectUsage(
  orgId: string,
  configurationIds: string[],
): Promise<CCOrgModelUnselectUsage[]> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.CONFIGURATIONS_USAGE(orgId)}`;
  const response = await authFetch(url, {
    method: 'POST',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({ configuration_ids: configurationIds }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch configuration usage: ${response.status}`);
  }

  const data: { entries: { configuration_id: string; domain_count: number }[] } = await response.json();
  return data.entries.map((e) => ({ configurationId: e.configuration_id, domainCount: e.domain_count }));
}

// GET .../utility-assignments — this org's current primary model per
// utility slot, with each owner's display name already resolved
// server-side. Pass configurationId (an existing configuration being
// edited) to narrow the response to that configuration's own type's slots;
// omit it to get all three — the save path reads the full current state to
// decide which single slots actually changed and need a per-utility PUT.
export async function fetchOrgUtilityAssignments(
  orgId: string,
  configurationId?: string,
): Promise<CCUtilityAssignmentEntry[]> {
  const qs = configurationId ? `?${new URLSearchParams({ configuration_id: configurationId }).toString()}` : '';
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.UTILITY_ASSIGNMENTS(orgId)}${qs}`;
  const response = await authFetch(url, { method: 'GET', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch utility assignments: ${response.status}`);
  }

  const data: {
    assignments: { utility: string; configuration_id: string | null; configuration_name: string | null }[];
  } = await response.json();
  return data.assignments.map((a) => ({
    utility: a.utility as CCModelUtility,
    configurationId: a.configuration_id,
    configurationName: a.configuration_name,
  }));
}

// PUT .../utility-assignments/{utility} — set (replace) a single utility
// slot's primary. Each utility is its own sub-resource: this fully replaces
// that one slot's value and never touches the others. There is no remove
// (no DELETE): a primary can be replaced but never cleared.
export async function setOrgUtilityAssignment(
  orgId: string,
  utility: CCModelUtility,
  configurationId: string,
): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${ORG_MODELS_ENDPOINTS.UTILITY_ASSIGNMENTS(orgId)}/${utility}`;
  const response = await authFetch(url, {
    method: 'PUT',
    headers: getHeadersWithOrg(),
    body: JSON.stringify({ configuration_id: configurationId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update utility assignment: ${response.status}`);
  }
}

// =============================================================================
// System Defaults API (global default model configuration per utility)
// =============================================================================

// GET /command-center/system-defaults — Adobe KG AI admin, global (no org_id).
export async function fetchSystemDefaults(): Promise<CCSystemDefaultEntry[]> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.SYSTEM_DEFAULTS}`;
  const response = await authFetch(url, { method: 'GET', headers: getHeadersWithOrg() });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to fetch system defaults: ${response.status}`);
  }

  const data: {
    defaults: {
      utility: string;
      configuration_id: string | null;
      configuration_name: string | null;
      provider: string | null;
      model: string | null;
      type: string | null;
      is_missing_required: boolean;
    }[];
  } = await response.json();
  return data.defaults.map((d) => ({
    utility: d.utility as CCModelUtility,
    configurationId: d.configuration_id,
    configurationName: d.configuration_name,
    provider: d.provider,
    model: d.model,
    type: d.type,
    isMissingRequired: d.is_missing_required,
  }));
}

// PUT /command-center/system-defaults — partial update: only the utility slots
// present in `defaults` are changed (a slot set to null clears it); omitted
// slots are left untouched. ConfigureModelDialog sends only changed slots.
export async function setSystemDefaults(defaults: CCSystemDefaults): Promise<void> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${CC_ENDPOINTS.SYSTEM_DEFAULTS}`;
  const response = await authFetch(url, {
    method: 'PUT',
    headers: getHeadersWithOrg(),
    body: JSON.stringify(defaults),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
    throw new Error(errorData.detail || errorData.error || `Failed to update system defaults: ${response.status}`);
  }
}
